/**
 * MapLegend — compact, collapsible on-map legend.
 *
 * The colour scales that used to clutter the 2D/3D "Layers" menu now live here,
 * on the map, where a legend belongs (Google Earth / ArcGIS / NASA FIRMS
 * pattern). Only sections for currently-active legend-bearing layers render;
 * with none active the component renders nothing. Collapses to a small pill so
 * it never obscures the map. See docs/3d-layers-menu-plan.md Phase B.
 */

import { useState } from 'react';
import './MapLegend.css';

export type LegendKey = 'hotspots' | 'aircraft' | 'ember' | 'fireSpread' | 'windArrows';

interface LegendDef {
  title: string;
  items: Array<{ color: string; label: string }>;
}

const LEGENDS: Record<LegendKey, LegendDef> = {
  hotspots: {
    title: 'Satellite hotspots (fire power)',
    items: [
      { color: '#ff1744', label: 'Extreme (500+ MW)' },
      { color: '#dc2626', label: 'Very High (200+ MW)' },
      { color: '#f97316', label: 'High (50+ MW)' },
      { color: '#fbbf24', label: 'Moderate (10+ MW)' },
      { color: '#fef08a', label: 'Low (<10 MW)' },
    ],
  },
  aircraft: {
    title: 'Aircraft',
    items: [
      { color: '#dc2626', label: 'Emergency / firefighting' },
      { color: '#2563eb', label: 'Other aircraft' },
    ],
  },
  ember: {
    title: 'Ember attack risk',
    items: [
      { color: '#dc2626', label: 'High (0–500 m)' },
      { color: '#f97316', label: 'Medium (500 m–5 km)' },
      { color: '#fbbf24', label: 'Low (5–15 km)' },
      { color: '#9ca3af', label: 'Possible (15 km+)' },
    ],
  },
  fireSpread: {
    title: 'Fire spread direction',
    items: [
      { color: '#dc2626', label: 'Head fire (fastest)' },
      { color: '#f97316', label: 'Flank fires (sides)' },
      { color: '#3b82f6', label: 'Back fire (slowest)' },
    ],
  },
  windArrows: {
    title: 'Wind speed',
    items: [
      { color: '#ef4444', label: 'Strong (>9 m/s)' },
      { color: '#eab308', label: 'Moderate (4.5–9 m/s)' },
      { color: '#06b6d4', label: 'Light (<4.5 m/s)' },
    ],
  },
};

interface MapLegendProps {
  /** Legend sections to show, in order. Empty → renders nothing. */
  activeKeys: LegendKey[];
}

export default function MapLegend({ activeKeys }: MapLegendProps) {
  const [expanded, setExpanded] = useState(false);
  const keys = activeKeys.filter((k) => LEGENDS[k]);
  if (keys.length === 0) return null;

  return (
    <div className={`map-legend${expanded ? ' expanded' : ''}`}>
      <button
        className="map-legend-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide map legend' : 'Show map legend'}
      >
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
        <span>Legend</span>
        <svg className="map-legend-chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points={expanded ? '6 9 12 15 18 9' : '18 15 12 9 6 15'} />
        </svg>
      </button>

      {expanded && (
        <div className="map-legend-body">
          {keys.map((k) => (
            <div key={k} className="map-legend-section">
              <div className="map-legend-title">{LEGENDS[k].title}</div>
              {LEGENDS[k].items.map((item) => (
                <div key={item.label} className="map-legend-item">
                  <span className="map-legend-dot" style={{ background: item.color }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
